import React, { useState } from 'react';
import { DeleteIcon, EditIcon, OperationsIcon } from '@common/utils/icons/icons';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FilterOperation } from 'models/enums/fleet-management';
import { useTranslation } from 'react-i18next';
import { FieldConfig } from 'models/fleet-management/FleetDataConfiguration';
import { WidgetField } from 'models/fleet-management/FleetWidget';
import { useAppSelector } from 'store';
import {
  selectFleetFieldsConfiguration,
  selectFleetOperationsConfiguration,
} from 'store/fleet-management/fleet-management-slice-selectors';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';

const FieldInput: React.FC<{
  value: string;
  operation?: FilterOperation | null;
  index: number;
  showOperation?: boolean;
  allowedOperations?: FilterOperation[];
  fields: FieldConfig[];
  onFieldChange?: (f: WidgetField, index: number, toDelete?: boolean) => void;
  onGroupChange?: (f: string, index: number, toDelete?: boolean) => void;
}> = ({
  index,
  operation,
  value,
  showOperation,
  allowedOperations,
  fields,
  onFieldChange,
  onGroupChange,
}) => {
  const { t } = useTranslation();

  const operationConfigs = useAppSelector((state) => selectFleetOperationsConfiguration(state));
  const fieldConfigs = useAppSelector((state) => selectFleetFieldsConfiguration(state));

  const [fieldsAnchorEl, setFieldsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [operationAnchorEl, setOperationAnchorEl] = useState<HTMLButtonElement | null>(null);

  const openFieldsMenuClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFieldsAnchorEl(event.currentTarget);
  };

  const closeFieldsMenuHandler = () => {
    setFieldsAnchorEl(null);
  };

  const openOperationMenuClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOperationAnchorEl(event.currentTarget);
  };

  const closeOperationMenuHandler = () => {
    setOperationAnchorEl(null);
  };

  const fieldChangeHandler = (newValue: string) => {
    if (onFieldChange != null) {
      onFieldChange({ name: newValue, operation: null }, index);
    }
    if (onGroupChange != null) {
      onGroupChange(newValue, index);
    }
    setFieldsAnchorEl(null);
  };

  const deleteFieldClickHandler = () => {
    if (onFieldChange != null) {
      onFieldChange({ name: value, operation: null }, index, true);
    }
    if (onGroupChange != null) {
      onGroupChange(value, index, true);
    }
  };

  const operationChangeHandler = (newOperation: FilterOperation) => {
    if (onFieldChange == null) return;
    setOperationAnchorEl(null);
    onFieldChange({ name: value, operation: newOperation }, index);
  };

  const translatedValue =
    t('fleet-management-field-' + value) !== 'fleet-management-field-' + value
      ? t('fleet-management-field-' + value)
      : value;

  return (
    <React.Fragment>
      <Stack
        direction="row"
        sx={(theme) => ({
          height: theme.spacing(5),
          width: '100%',
          borderRadius: 1,
          backgroundColor: theme.palette.action.selected,
        })}
        alignItems="center"
        spacing={1}
      >
        <Typography flex={1} px={2}>
          {translatedValue}
        </Typography>
        {showOperation &&
          (operation == null ? (
            <TooltipIconButton
              icon={<OperationsIcon color="action" />}
              tooltip={t('input-label-widget-operation')}
              onClick={openOperationMenuClickHandler}
            />
          ) : (
            <Button
              onClick={openOperationMenuClickHandler}
              size="small"
              sx={{ backgroundColor: '#FFF', boxShadow: 0 }}
            >
              {operation}
            </Button>
          ))}
        <IconButton color="default" onClick={openFieldsMenuClickHandler}>
          <EditIcon />
        </IconButton>
        <IconButton color="error" onClick={deleteFieldClickHandler}>
          <DeleteIcon />
        </IconButton>
      </Stack>

      {fieldsAnchorEl != null && (
        <Menu
          MenuListProps={{
            'aria-labelledby': 'fade-button',
          }}
          anchorEl={fieldsAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={fieldsAnchorEl != null}
          onClose={closeFieldsMenuHandler}
        >
          <Box
            sx={(theme) => ({
              width: theme.spacing(32),
              maxHeight: theme.spacing(24),
            })}
          >
            {fields.map((f) => (
              <MenuItem
                key={f.fieldName}
                value={f.fieldName}
                onClick={() => fieldChangeHandler(f.fieldName)}
              >
                {t('fleet-management-field-' + f.fieldName)}
              </MenuItem>
            ))}
          </Box>
        </Menu>
      )}
      {operationAnchorEl != null && (
        <Menu
          MenuListProps={{
            'aria-labelledby': 'fade-button',
          }}
          anchorEl={operationAnchorEl}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open={operationAnchorEl != null}
          onClose={closeOperationMenuHandler}
        >
          <Box
            sx={(theme) => ({
              width: theme.spacing(32),
              maxHeight: theme.spacing(24),
            })}
          >
            {(
              operationConfigs?.find(
                (oC) => oC.type === fieldConfigs?.find((f) => f.fieldName === value)?.type,
              )?.operations ?? []
            )
              .filter((o) => allowedOperations == null || allowedOperations.includes(o))
              .map((operation) => (
                <MenuItem
                  key={operation}
                  value={operation}
                  onClick={() => operationChangeHandler(operation)}
                >
                  {operation}
                </MenuItem>
              ))}
          </Box>
        </Menu>
      )}
    </React.Fragment>
  );
};

export default FieldInput;
